import { actionItemsService } from "../../services/api/action-items.endpoints";
import { pageBuildingService } from "../../services/api/page-building.endpoints";
import { selectUserWebsitePermissions } from "../../features/userSlice";
import { WebsiteWithUuid } from "../../data/website/Website";
import store from "../../store";

async function createNewTasks(oauthTokenHeader: string, data: any) {
  try {
    const response = await actionItemsService.createActionItem(
      oauthTokenHeader,
      data,
    );
    // Ensure response exists and check for both 200 and 201
    if (!response || !response.status) {
      return { reason: "Failed to create new task." };
    }

    if (response.status === 200) {
      return response;
    } else if (response.status === 201) {
      return response;
    }
  } catch (e: any) {
    throw e;
  }
}

// True when the website's cache_type is "default"; variants are not allowed there.
// cache_type comes from the logged-in user's website permissions.
const isDefaultCacheSite = (websiteSharedUid: string) => {
  const websitePermissions: WebsiteWithUuid[] | null =
    selectUserWebsitePermissions(store.getState());
  const siteCacheType = websitePermissions?.find(
    (website) => Object.keys(website)[0] === websiteSharedUid,
  )?.[websiteSharedUid]?.cache_type;
  return siteCacheType === "default";
};

// Shared call to the variants endpoint, used by every module that needs to sync
// variants for an entity (page, page_group, ...). Skipped when the website's
// cache_type is "default", unless `skipCacheTypeCheck` is set (Page Builder module).
// `widgets` is only sent when provided.
// Failures are swallowed so the caller's main save/publish flow is never blocked.
async function updateVariants({
  oauthTokenHeader,
  websiteSharedUid,
  entityType,
  entitySharedUid,
  widgets,
  skipCacheTypeCheck = false,
}: {
  oauthTokenHeader: string | null | undefined;
  websiteSharedUid: string | null | undefined;
  entityType: string;
  entitySharedUid: string | null | undefined;
  widgets?: any;
  skipCacheTypeCheck?: boolean;
}) {
  if (!oauthTokenHeader || !websiteSharedUid || !entitySharedUid) return null;

  if (!skipCacheTypeCheck && isDefaultCacheSite(websiteSharedUid)) return null;

  const payload = {
    data: {
      entity_type: entityType,
      entity_shared_uid: entitySharedUid,
      ...(widgets !== undefined && { widgets }),
    },
  };

  try {
    return await pageBuildingService.updateVariants(
      payload,
      websiteSharedUid,
      oauthTokenHeader,
    );
  } catch (e: any) {
    return null;
  }
}

// Shared call to remove a page's variants, used when a page is deleted or moved
// out of "publish". Skipped when the website's cache_type is "default", unless
// `skipCacheTypeCheck` is set (Page Builder module). Sends a blank body.
// Failures are swallowed so the caller's main flow is never blocked.
async function removeVariants({
  oauthTokenHeader,
  websiteSharedUid,
  pageSharedUid,
  skipCacheTypeCheck = false,
}: {
  oauthTokenHeader: string | null | undefined;
  websiteSharedUid: string | null | undefined;
  pageSharedUid: string | null | undefined;
  skipCacheTypeCheck?: boolean;
}) {
  if (!oauthTokenHeader || !websiteSharedUid || !pageSharedUid) return null;

  if (!skipCacheTypeCheck && isDefaultCacheSite(websiteSharedUid)) return null;

  try {
    return await pageBuildingService.removeVariants(
      websiteSharedUid,
      pageSharedUid,
      oauthTokenHeader,
    );
  } catch (e: any) {
    return null;
  }
}

const GenericEndPointsLogic = {
  createNewTasks,
  updateVariants,
  removeVariants,
};

export default GenericEndPointsLogic;
